/**
 * Month helpers shared by the P&L grid and the projection grids. Every
 * date that lives in `projections_*.month` (and in the v_monthly_pnl
 * view) is the first-of-month UTC ISO date string. We center all the
 * arithmetic on UTC to avoid the "is this Jan or Dec" timezone bug.
 */

const MONTH_LABEL = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  year: '2-digit',
  timeZone: 'UTC',
});

/** YYYY-MM-01 string for the first-of-month UTC of `d`. */
export function toMonthKey(d: Date): string {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-01`;
}

/** Mar '26 — short label for table column headers. */
export function formatMonthLabel(monthIso: string): string {
  return MONTH_LABEL.format(new Date(monthIso));
}

/** Build N consecutive months ending at (or starting from) the current month. */
export function buildMonthRange({
  from,
  count,
}: {
  /** 'past' anchors `count` months ending at the current month, 'future'
   *  anchors `count` months starting from the current month. */
  from: 'past' | 'future';
  count: number;
}): string[] {
  const months: string[] = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const offset = from === 'past' ? -(count - 1 - i) : i;
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
    months.push(toMonthKey(d));
  }
  return months;
}

/** Normalise a value the DB returned (timestamp or date) to YYYY-MM-01. */
export function normaliseMonth(raw: string): string {
  return raw.slice(0, 7) + '-01';
}

/**
 * Calendar-aware month-range presets pinned to the current UTC year. YTD
 * walks Jan → current month; Q1..Q4 always return the three months of
 * that quarter regardless of where "today" sits; `year0`/`year1`/`year2`
 * return the full twelve months of the current year, next year, and the
 * year after — handy for watching projections extrapolate across whole
 * calendar years without counting months.
 */
export type PeriodPreset =
  | 'ytd'
  | 'q1'
  | 'q2'
  | 'q3'
  | 'q4'
  | 'year0'
  | 'year1'
  | 'year2';

export function buildMonthRangeForPeriod(period: PeriodPreset): string[] {
  const now = new Date();
  const year = now.getUTCFullYear();
  const months: string[] = [];

  if (period === 'ytd') {
    const lastMonth = now.getUTCMonth();
    for (let m = 0; m <= lastMonth; m++) {
      months.push(toMonthKey(new Date(Date.UTC(year, m, 1))));
    }
    return months;
  }

  if (period === 'year0' || period === 'year1' || period === 'year2') {
    const offset = Number(period.slice(4));
    const targetYear = year + offset;
    for (let m = 0; m < 12; m++) {
      months.push(toMonthKey(new Date(Date.UTC(targetYear, m, 1))));
    }
    return months;
  }

  const startMonth = (Number(period.slice(1)) - 1) * 3;
  for (let m = startMonth; m < startMonth + 3; m++) {
    months.push(toMonthKey(new Date(Date.UTC(year, m, 1))));
  }
  return months;
}

/** The current UTC calendar year — useful for labeling year presets in the UI. */
export function currentYear(): number {
  return new Date().getUTCFullYear();
}
